// Start core for the New analysis panel.
//
// buildStartRequest() rebuilds, as pure functions, the exact request the analysis
// wizard (src/views/AnalysisView/AnnotationView/index.js) sends for Germline and
// Somatic runs: handleStandardSubmit (FASTQ and VCF), handleOntSubmit and
// handlePacbioSubmit, posted by useFileActions.handleFileAnnotation as
// { fileId, annotation_type, annotation }. The single addition is a top-level
// `batch` object when the start belongs to a batch. The backend stores the whole
// body verbatim as AnalysisWrapper.analysis_parameters (libra/views.py,
// GenericAnnotateView, every branch), which is how batches are grouped later.
//
// startAnalyses() posts one sample at a time, never in parallel, with a local axios
// call per start. It does not use the module-level AbortController in
// src/api/analysis/annotate.js: that one aborts the previous start whenever a new
// one begins. (axios 0.21 ignores `signal` anyway, so a start in flight is never
// cancelled; the signal here only stops the loop between samples. Cancelling an
// in-flight start would not stop the server and could lead to a double start on retry.)
//
// Shapes (see the shared contract in the batch design spec):
//
// workflow = {
//   analysis: 'germline' | 'somatic',
//   input: 'fastq' | 'ont' | 'pacbio' | 'vcf',
//   mode: 'to' | 'tn',                   somatic FASTQ only
//   seq: 'wes' | 'ces' | 'wgs',          FASTQ only
//   kitId: string,                       the BedFile `name` (what the wizard's kit
//                                        select holds and sends as `panel`), not its id
//   kitName?: string,                    BedFile.visible_name, for describeWorkflow only
//                                        (kitLabel is read too)
//   cnv: bool, cnvMethod, cnvCohortId,   germline FASTQ exome only; cnvCohortId is a
//                                        cohort id or GENESUS_AUTOMATIC_COHORT
//   lrCnv, lrSv, lrMeth: bool,           ONT / PacBio only
//   reference: 'GRCh38' | 'GRCh37',      VCF only; null or 'None' until chosen
//   tumourType: OncoTree node | null,    somatic; the OncotreeSelect value
//                                        ({ oncotree_code, name, ... })
//   interpretation: 'standard' | 'ai',   germline only
// }
//
// sample = {
//   key,                    sample name, also batch.sampleKey
//   fileId,                 main file id the wizard sends: fastq_pair_id, vcf_id,
//                           ont_bam_id or pacbio_bam_id
//   fileType,               'FASTQ' | 'VCF' | 'ONT_BAM' | 'PACBIO_BAM' (or the /file/list
//                           file_type: 'fastq' | 'vcf' | 'ont_bam' | 'pacbio_bam')
//   files, alias,
//   fileIds?,               every lane's fastq_pair_id, the primary included (laneFileIds,
//                           or an array in `lanes`, are read too). For germline FASTQ, two
//                           or more send isDemultiplexed + allSelectedFiles, as the
//                           wizard's "Demultiplexed Sample" mode. Somatic DNA FASTQ sends
//                           every lane on every start (somaticLaneFields).
//   lanes?,                 lane count
//   variantType?,           VCF only: the file's variant_type ('snp_indel' | 'cnv' | 'sv')
//   methylationReady?,      PacBio only: the file's methylation_available is
//                           'methylation_ready' (methylationAvailable is read too)
//   sex, age, onsetAge, notes, changed: { sex?, age?, onsetAge?, notes? },
//   hpo: [{ id: 'HP:0001250', name: 'Seizure' }], clinicalHistory, aiGuidance,
//   diseases?,              optional, same objects the wizard's DiseaseChips hold
//   tumourType?,            per-sample override of workflow.tumourType
//   normalFileId, normalKey,
//   normalFileIds?,         every lane's fastq_pair_id of the normal, normalFileId included;
//                           tumour-normal DNA FASTQ sends them as normalAllSelectedFiles
//   ai?,                    this sample's own AI choice: true or false over
//                           workflow.interpretation; undefined follows the batch
//                           (sampleRunsAI). Only which samples run AI changes, never
//                           the payload's shape.
//   excludeAI,              older drafts: true reads as ai false
//   excludeCNV,
// }
//
// batch = { id (uuid), name }. The body's `batch` field adds sampleKey, the
// workflowSummary (describeWorkflow) and, when set, sampleAlias.
//
// Long-read samples may carry longRead = { cnv?, sv?, meth? } | null: a per-sample
// override of the batch's lrCnv / lrSv / lrMeth (effectiveLongRead).
//
// Group analyses (workflow.analysis 'multisample' | 'cohort' | 'carrier') start one
// request per group, not per sample. Their requests come from buildMultisampleRequest,
// buildCohortRequest and buildCarrierRequest, and are posted by startRequests().
// startAnalyses() refuses them. Deliberate differences from the wizard, each
// covered by a test:
//   Multisample  relation required and sent trimmed; at least one member included;
//                one analysis at most once per group; analysisName is the group name
//                when typed, else the wizard's `Multisample - <date>`.
//   Cohort       2 to 50 analyses for both methods (the wizard allowed 1).
//   Carrier      exactly samples_required members (the backend demands equality).
//   Errors       a 400/500 reply is "not started", 402 "not enough balance", no reply
//                "unknown". The wizard showed success on any non-402 error.

import { IMPORT_FAILED_TEXT, isImportRefusal } from 'src/utils/importRefusal'
import { v4 as uuidv4 } from 'uuid'
import axios from '../utils/axios'
import { API_BASE_URL } from '../constants'
import { updateDetails } from '../api/file/details'
import { EMPTY_SPECIMEN, buildSpecimenPayload, validateSpecimen } from '../views/AnalysisView/lib/specimen'
import {
  EMPTY_LIBRARY,
  RNA_ANALYSIS_TYPE,
  RNA_REFERENCE,
  RNA_CAPTURE_OFF,
  buildRnaIntakeFields,
  rnaPanelOf,
  validateLibrary,
} from '../views/AnalysisView/lib/rnaLibrary'

// Must match GENESUS_AUTOMATIC_COHORT in AnnotationView/components/StepConfigure.jsx
// (kept here so this module does not import a UI component). It stands for "build
// the cohort on demand" and is never sent: the payload leaves cohort_id out instead.
export const GENESUS_AUTOMATIC_COHORT = 'genesus_automatic'

// The /file/details model field is varchar(256).
export const NOTES_MAX_LENGTH = 256

export const WORKFLOW_DEFAULTS = Object.freeze({
  analysis: 'germline',
  input: 'fastq',
  mode: 'to',
  // The wizard has no default sequencing type or reference genome: both must be chosen.
  seq: null,
  kitId: '',
  kitName: '',
  cnv: false,
  cnvMethod: 'genesus-cnv',
  cnvCohortId: GENESUS_AUTOMATIC_COHORT,
  lrCnv: false,
  lrSv: false,
  lrMeth: false,
  reference: null,
  tumourType: null,
  interpretation: 'standard',
  // Group analyses.
  cohortMethod: 'genesus-cnv',
  cohortName: '',
  carrierConfigId: null,
  carrierConfigTitle: '',
  carrierSamplesRequired: null,
})

// Analyses that start one request per sample, and those that start one per group.
export const FILE_ANALYSES = Object.freeze(['germline', 'somatic'])
export const GROUP_ANALYSES = Object.freeze(['multisample', 'cohort', 'carrier'])
const isGroupWorkflow = (wf) => GROUP_ANALYSES.includes(wf?.analysis)

// The wizard's CNV methods, in its order (StepConfigure).
export const CNV_METHODS = [
  { value: 'genesus-cnv', label: 'Genesus CNV', recommended: true },
  { value: 'ecole', label: 'ECOLE' },
  { value: 'exomedepth', label: 'ExomeDepth' },
]

export const REFERENCE_GENOMES = [
  { value: 'GRCh38', label: 'GRCh38 (hg38)' },
  { value: 'GRCh37', label: 'GRCh37 (hg19)' },
]

// seq -> the wizard's sequencingType value (sent as targetedType) and a display label.
const SEQUENCING = {
  wes: { wizard: 'Whole Exome', label: 'Whole exome', short: 'WES' },
  ces: { wizard: 'Clinical Exome', label: 'Clinical exome', short: 'CES' },
  wgs: { wizard: 'Whole Genome', label: 'Whole genome', short: 'WGS' },
}

const CNV_TYPES = {
  'genesus-cnv': 'GENESUS-CNV',
  exomedepth: 'EXOMEDEPTH',
  ecole: 'ECOLE',
}

const INPUT_TO_ANNOTATION_TYPE = { fastq: 'FASTQ', ont: 'ONT_BAM', pacbio: 'PACBIO_BAM', vcf: 'VCF' }

const FILE_TYPE_ALIASES = {
  fastq: 'FASTQ',
  vcf: 'VCF',
  ont_bam: 'ONT_BAM',
  ont: 'ONT_BAM',
  pacbio_bam: 'PACBIO_BAM',
  pacbio: 'PACBIO_BAM',
}

const INPUT_LABELS = { FASTQ: 'FASTQ', VCF: 'VCF', ONT_BAM: 'ONT BAM', PACBIO_BAM: 'PacBio HiFi BAM' }

const PATIENT_FIELDS = ['sex', 'age', 'onsetAge', 'notes']
// /file/details reads a VcfFile for 'VCF' and a FastqFilePair for anything else, so
// ONT and PacBio files cannot be saved through it.
const DETAILS_FILE_TYPES = ['FASTQ', 'VCF']
const KARYOTYPES = ['XX', 'XY', 'XXX', 'XYY', 'XXY', 'XXXX', 'XXXY', 'XXYY', 'XXXXX', 'XXXXY']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const startUrl = () => `${API_BASE_URL}/analysis/genericannotate`

const withDefaults = (workflow) => ({ ...WORKFLOW_DEFAULTS, ...(workflow || {}) })

const isSomatic = (wf) => wf.analysis === 'somatic'
const isExome = (seq) => seq === 'wes' || seq === 'ces'

// ---------------------------------------------------------------------------
// Sample helpers

export const normaliseFileType = (fileType) => {
  if (!fileType) return null
  const raw = String(fileType)
  if (INPUT_LABELS[raw]) return raw
  return FILE_TYPE_ALIASES[raw.toLowerCase()] || null
}

// The request's annotation_type. The wizard derives it from the file itself
// (useFileActions.handleFileAnnotation), so the sample's own type wins over the
// workflow input; validateSample flags a mismatch.
export const annotationTypeOf = (workflow, sample) =>
  normaliseFileType(sample?.fileType) || INPUT_TO_ANNOTATION_TYPE[withDefaults(workflow).input] || null

// As the wizard: a VCF without a declared variant type is SNV/Indel.
const vcfVariantType = (sample) => sample?.variantType || 'snp_indel'

// Every lane's fastq_pair_id, primary first, without duplicates.
const laneIdsOf = (sample) => {
  const raw = [sample?.fileIds, sample?.laneFileIds, sample?.lanes].find(Array.isArray) || []
  const ids = raw
    .map((lane) => (typeof lane === 'string' ? lane : lane?.fileId || lane?.fastq_pair_id || lane?.id))
    .filter(Boolean)
  const rest = ids.filter((id, i) => id !== sample?.fileId && ids.indexOf(id) === i)
  return sample?.fileId ? [sample.fileId, ...rest] : rest
}

const laneCountOf = (sample) => Math.max(laneIdsOf(sample).length, typeof sample?.lanes === 'number' ? sample.lanes : 0)

// Every lane's fastq_pair_id of a tumour's matched normal, normalFileId first.
export const normalLaneIdsOf = (sample) => {
  const ids = (Array.isArray(sample?.normalFileIds) ? sample.normalFileIds : []).filter(Boolean)
  const rest = ids.filter((id, i) => id !== sample?.normalFileId && ids.indexOf(id) === i)
  return sample?.normalFileId ? [sample.normalFileId, ...rest] : rest
}

const methylationReady = (sample) =>
  sample?.methylationReady === true || sample?.methylationAvailable === 'methylation_ready'

const REFERENCE_VALUES = ['GRCh38', 'GRCh37']
const hasReference = (wf) => REFERENCE_VALUES.includes(wf.reference)

const hpoTermsOf = (sample) =>
  (Array.isArray(sample?.hpo) ? sample.hpo : [])
    .map((term) => ({ id: term?.id || term?.hpo_id, name: term?.name || term?.id || term?.hpo_id }))
    .filter((term) => term.id)

// The wizard's disease transforms (handleStandardSubmit / handleOntSubmit).
const diseasesOf = (sample) => {
  const list = Array.isArray(sample?.diseases) ? sample.diseases : []
  return {
    names: list.map((d) => d?.disease || d?.name || d?.label || d),
    ids: list.map((d) => d?.omim || d?.id),
    keys: list.map((d) => d?.key || d?.id),
  }
}

const tumourTypeOf = (wf, sample) => {
  const node = sample?.tumourType || wf.tumourType
  if (!node) return { code: '', name: '' }
  if (typeof node === 'string') return { code: node.trim(), name: '' }
  return { code: node.oncotree_code || node.code || '', name: node.name || '' }
}

// CNV is offered only for germline FASTQ exome runs (StepConfigure showCnvOptions;
// the backend also drops it for anything but Germline + Targeted).
// A tumour RNA analysis: somatic FASTQ in mode 'rna'. Tumour-only, a library
// instead of a sequencing type and kit, aligned on the GRCh38 RNA reference.
export const isTumourRna = (workflow) => {
  const wf = withDefaults(workflow)
  return isSomatic(wf) && wf.input === 'fastq' && wf.mode === 'rna'
}

// The library of an RNA sample, in the wizard's EMPTY_LIBRARY shape.
export const libraryOf = (sample) => ({ ...EMPTY_LIBRARY, ...(sample?.library || {}) })

// A sample may carry several lane pairs in germline FASTQ and somatic DNA FASTQ
// (tumour-only and tumour-normal). Tumour RNA takes one pair per sample.
export const lanesAllowed = (workflow) => {
  const wf = withDefaults(workflow)
  if (wf.input !== 'fastq') return false
  return wf.analysis === 'germline' || (isSomatic(wf) && !isTumourRna(wf))
}

export const cnvAvailable = (workflow) => {
  const wf = withDefaults(workflow)
  return wf.analysis === 'germline' && wf.input === 'fastq' && isExome(wf.seq)
}

// AI never runs for somatic analyses or CNV/SV VCFs (AnnotationView aiUnavailable).
export const aiAvailable = (workflow, sample) => {
  const wf = withDefaults(workflow)
  if (isSomatic(wf)) return false
  if (sample && annotationTypeOf(wf, sample) === 'VCF') {
    const variantType = vcfVariantType(sample)
    if (variantType === 'cnv' || variantType === 'sv') return false
  }
  return true
}

/**
 * Whether AI runs for this sample: its own choice (sample.ai true or false) where
 * set, the batch's interpretation otherwise. The older excludeAI: true reads as
 * false. Never where AI is not available (somatic runs, CNV and SV VCFs).
 */
export const sampleRunsAI = (workflow, sample) => {
  const wf = withDefaults(workflow)
  if (wf.analysis !== 'germline' || !aiAvailable(wf, sample)) return false
  if (typeof sample?.ai === 'boolean') return sample.ai
  if (sample?.excludeAI) return false
  return wf.interpretation === 'ai'
}

const runAIFor = sampleRunsAI
const runCnvFor = (wf, sample) => Boolean(wf.cnv) && cnvAvailable(wf) && !sample?.excludeCNV

// ---------------------------------------------------------------------------
// Long-read options per sample

const LONG_READ_FIELDS = { cnv: 'lrCnv', sv: 'lrSv', meth: 'lrMeth' }

/**
 * The CNV, SV and methylation a long-read sample asks for: its own override
 * (sample.longRead) where set, the batch's lrCnv / lrSv / lrMeth otherwise.
 * Methylation readiness is not applied here; see effectiveLongRead.
 */
export function requestedLongRead(workflow, sample) {
  const wf = withDefaults(workflow)
  const own = sample?.longRead && typeof sample.longRead === 'object' ? sample.longRead : {}
  const pick = (key) => (typeof own[key] === 'boolean' ? own[key] : Boolean(wf[LONG_READ_FIELDS[key]]))
  return { cnv: pick('cnv'), sv: pick('sv'), meth: pick('meth') }
}

/**
 * What actually runs for a long-read sample: requestedLongRead, with PacBio
 * methylation only on a methylation_ready file (the wizard's rule). ONT methylation
 * runs as requested.
 * @returns {{ cnv: boolean, sv: boolean, meth: boolean }}
 */
export function effectiveLongRead(workflow, sample) {
  const wf = withDefaults(workflow)
  const requested = requestedLongRead(wf, sample)
  const pacbio = annotationTypeOf(wf, sample) === 'PACBIO_BAM'
  return { ...requested, meth: requested.meth && (!pacbio || methylationReady(sample)) }
}

// ---------------------------------------------------------------------------
// Payload builders

// Specimen metadata for a somatic start, in the shape the wizard's SpecimenFields kept:
// the specimen types. Sex and age are not sent (the somatic run infers sex from the
// reads and reads no age).
export const specimenOf = (sample) => ({ ...EMPTY_SPECIMEN, ...(sample?.specimen || {}) })

// The lanes of a somatic DNA FASTQ start: the tumour's (laneIdsOf, fileId first) as
// allSelectedFiles and, tumour-normal only, the normal's (normalLaneIdsOf,
// normalSampleId first) as normalAllSelectedFiles. The backend reads both lists on
// every TumorOnly and TumorNormal start and refuses normal lanes on TumorOnly, so a
// tumour-only start never sends them. isDemultiplexed is not read there; it is sent
// as germline sends it. Every somatic lane field of the request is set here and
// nowhere else.
export const somaticLaneFields = (sample, { tumourNormal = false } = {}) => {
  const lanes = laneIdsOf(sample)
  return {
    isDemultiplexed: lanes.length > 1,
    allSelectedFiles: lanes,
    ...(tumourNormal ? { normalAllSelectedFiles: normalLaneIdsOf(sample) } : {}),
  }
}

// handleStandardSubmit: germline and somatic, FASTQ and VCF.
const buildStandardAnnotation = (wf, sample, annotationType, rnaCapture = RNA_CAPTURE_OFF) => {
  const isVcf = annotationType === 'VCF'
  const somatic = isSomatic(wf)
  const tumourNormal = somatic && !isVcf && wf.mode === 'tn'
  const wholeGenome = wf.seq === 'wgs'
  const runAI = runAIFor(wf, sample)
  const runCnv = runCnvFor(wf, sample)
  const variantType = isVcf ? vcfVariantType(sample) : 'snp_indel'
  const hpo = hpoTermsOf(sample)
  const diseases = diseasesOf(sample)
  const tumour = somatic ? tumourTypeOf(wf, sample) : { code: '', name: '' }
  // isDemultiplexed and allSelectedFiles here are germline FASTQ's. Somatic DNA FASTQ
  // sets both in somaticLaneFields; tumour RNA leaves isDemultiplexed undefined and
  // sends no lanes.
  const lanes = !somatic && !isVcf ? laneIdsOf(sample) : []
  const demultiplexed = lanes.length > 1
  const cohortId =
    runCnv &&
    (wf.cnvMethod === 'genesus-cnv' || wf.cnvMethod === 'exomedepth') &&
    wf.cnvCohortId &&
    wf.cnvCohortId !== GENESUS_AUTOMATIC_COHORT
      ? wf.cnvCohortId
      : undefined

  const rna = !isVcf && isTumourRna(wf)
  const specimenPayload = somatic ? buildSpecimenPayload(specimenOf(sample), { paired: tumourNormal }) : undefined
  const linkedAnalyses = somatic ? sample?.linkedAnalyses || [] : []
  // Sharing a linked analysis's tumour specimen sends no specimen of its own.
  const rnaFields = rna ? buildRnaIntakeFields({ library: libraryOf(sample), linkedAnalyses, specimenPayload }) : null

  return {
    reference: isVcf ? wf.reference : rna ? RNA_REFERENCE : 'None',
    runAI,
    hpos: hpo.map((term) => term.id),
    hpoNames: hpo.map((term) => term.name),
    diseases: diseases.names,
    diseaseIds: diseases.ids,
    diseaseKeys: diseases.keys,
    clinicalHistory: sample?.clinicalHistory || '',
    userGuidance: sample?.aiGuidance || '',
    isSnp: variantType === 'snp_indel',
    isCnv: variantType === 'cnv',
    isSv: variantType === 'sv',
    type: isVcf || rna ? undefined : wholeGenome ? 'WholeGenome' : 'Targeted',
    targetedType: isVcf || rna || wholeGenome ? '' : SEQUENCING[wf.seq]?.wizard || '',
    // RNA: the targeted capture library's panel, only where the backend accepts one
    // (GET rna-vocabulary capture_panels_enabled); every other RNA library sends none.
    panel: rna ? rnaPanelOf(libraryOf(sample), rnaCapture) : isVcf || wholeGenome ? '' : wf.kitId || '',
    analysisType: isVcf
      ? undefined
      : rna
      ? RNA_ANALYSIS_TYPE
      : somatic
      ? tumourNormal
        ? 'TumorNormal'
        : 'TumorOnly'
      : 'Germline',
    library: rnaFields ? rnaFields.library : undefined,
    isSomatic: isVcf && somatic,
    oncotreeCode: tumour.code,
    oncotreeName: tumour.name,
    normalSampleId: tumourNormal ? sample?.normalFileId || '' : '',
    runCnv,
    cnv_type: runCnv ? CNV_TYPES[wf.cnvMethod] : undefined,
    cohort_id: cohortId,
    isDemultiplexed: somatic ? undefined : demultiplexed,
    allSelectedFiles: demultiplexed ? lanes : [],
    // Somatic only, as the wizard sent them: specimen metadata (undefined when nothing
    // was entered) and the patient's other analyses. The backend validates both
    // before any charge and links the matched normal's analyses itself.
    specimen: rnaFields ? rnaFields.specimen : specimenPayload,
    linkedAnalysisIds: somatic ? linkedAnalyses.map((a) => a.analysis_id) : undefined,
    ...(somatic && !isVcf && !rna ? somaticLaneFields(sample, { tumourNormal }) : {}),
  }
}

// handleOntSubmit / handlePacbioSubmit, with the sample's own long-read options.
const buildLongReadAnnotation = (wf, sample, pacbio) => {
  const hpo = hpoTermsOf(sample)
  const diseases = diseasesOf(sample)
  // PacBio methylation can only be switched on for a methylation_ready file.
  const options = effectiveLongRead(wf, sample)
  return {
    runAI: runAIFor(wf, sample),
    runCnv: options.cnv,
    runSv: options.sv,
    runMod: options.meth,
    ...(pacbio ? { annotation_type: 'PACBIO_BAM' } : {}),
    hpos: hpo.map((term) => term.id),
    hpoNames: hpo.map((term) => term.name),
    diseases: diseases.names,
    diseaseKeys: diseases.keys,
    clinicalHistory: sample?.clinicalHistory || '',
    userGuidance: sample?.aiGuidance || '',
  }
}

// workflowSummary is what the batch cards and page show. Reading it back from the
// annotation would name a user-uploaded kit by its storage name (<uuid>.bed), since
// the payload only carries BedFile.name; describeWorkflow uses its visible name.
const batchFieldFor = (batch, sample, wf) => {
  const alias = typeof sample?.alias === 'string' ? sample.alias.trim() : ''
  return {
    id: batch.id,
    name: batch.name || '',
    sampleKey: sample?.key ?? null,
    workflowSummary: describeWorkflow(wf),
    ...(alias ? { sampleAlias: alias } : {}),
  }
}

/**
 * The request the wizard would send for this workflow and sample. Pure.
 * `rnaCapture` is rnaCaptureOf(GET rna-vocabulary): the RNA target panel is sent
 * only with it on.
 * @returns {{ url: string, body: { fileId, annotation_type, annotation, batch? } }}
 */
export function buildStartRequest(workflow, sample, batch, { rnaCapture = RNA_CAPTURE_OFF } = {}) {
  const wf = withDefaults(workflow)
  const annotationType = annotationTypeOf(wf, sample)
  let annotation
  if (annotationType === 'ONT_BAM') annotation = buildLongReadAnnotation(wf, sample, false)
  else if (annotationType === 'PACBIO_BAM') annotation = buildLongReadAnnotation(wf, sample, true)
  else annotation = buildStandardAnnotation(wf, sample, annotationType, rnaCapture)

  const body = { fileId: sample?.fileId, annotation_type: annotationType, annotation }
  if (batch && batch.id) body.batch = batchFieldFor(batch, sample, wf)
  return { url: startUrl(), body }
}

// ---------------------------------------------------------------------------
// Multisample: one analysis per group of finished analyses (the wizard's
// handleMultisampleSubmit and startMultisampleAnalysis in src/api/analysis/annotate.js).
//
// group = { uid, name, members: [{ uid, fileId, relation, include, gt, done?, ... }] }
// fileId is the finished analysis row's file_id.

export const MULTISAMPLE_RELATIONS = Object.freeze([
  'Proband',
  'Mother',
  'Father',
  'Sibling',
  'Grandparent',
  'Aunt',
  'Uncle',
  'Cousin',
  'Son',
  'Daughter',
])

export const GENOTYPE_OPTIONS = Object.freeze([
  { value: 'any', label: 'Any' },
  { value: 'heterozygote', label: 'Heterozygote' },
  { value: 'homozygote', label: 'Homozygote' },
])

// Analyses that cannot be a multisample member (the wizard's list leaves them out).
export const MULTISAMPLE_EXCLUDED_TYPES = Object.freeze(['GENESUS-COHORT', 'EXOMEDEPTH-COHORT', 'CARRIER'])

const membersOf = (group) => (Array.isArray(group?.members) ? group.members.filter(Boolean) : [])
const trimmed = (value) => (value === null || value === undefined ? '' : String(value).trim())
// The backend reads a missing `include` as included (build_multisample_parquet).
const isIncluded = (member) => member?.include !== false

/** The group's name when typed, otherwise exactly the wizard's name. */
export function multisampleAnalysisName(group, date = new Date()) {
  return trimmed(group?.name) || `Multisample - ${date.toLocaleString()}`
}

/**
 * The request the wizard would send for one multisample group. Members without a
 * file are dropped, relations are trimmed, and an excluded member's genotype is
 * sent as 'any' (the wizard resets it on exclude).
 * @returns {{ url: string, body: { annotation_type: 'MULTISAMPLE', annotation, analysisName } }}
 */
export function buildMultisampleRequest(group, { date } = {}) {
  const annotation = membersOf(group)
    .filter((member) => member.fileId)
    .map((member) => {
      const include = isIncluded(member)
      return {
        fileId: member.fileId,
        relation: trimmed(member.relation),
        include,
        gt: include ? member.gt || 'any' : 'any',
      }
    })
  return {
    url: startUrl(),
    body: { annotation_type: 'MULTISAMPLE', annotation, analysisName: multisampleAnalysisName(group, date) },
  }
}

/**
 * Problems that keep a multisample group from starting. Every one blocks.
 * @returns {Array<{ code, message, blocking: true, memberUid? }>}
 */
export function validateMultisampleGroup(group) {
  const issues = []
  const add = (code, message, memberUid) =>
    issues.push({ code, message, blocking: true, ...(memberUid !== undefined ? { memberUid } : {}) })
  const members = membersOf(group).filter((member) => member.fileId)
  if (members.length < 2) add('too_few', 'Add at least 2 samples')
  const seen = new Set()
  members.forEach((member) => {
    const id = String(member.fileId)
    if (seen.has(id)) add('duplicate', 'Same analysis twice', member.uid)
    seen.add(id)
    if (!trimmed(member.relation)) add('no_relation', 'Relation missing', member.uid)
    if (member.done === false) add('not_finished', 'Analysis not finished yet', member.uid)
  })
  if (members.length && !members.some(isIncluded)) add('none_included', 'Include at least one sample')
  return issues
}

// ---------------------------------------------------------------------------
// CNV cohort: a reference set of finished exome analyses on one capture kit (the
// wizard's handleCohortSubmit and src/api/analysis/cohort.js).

export const COHORT_MIN = 2
export const COHORT_MAX = 50
export const COHORT_RECOMMENDED = 30

export const COHORT_METHOD_OPTIONS = Object.freeze([
  { value: 'genesus-cnv', label: 'Genesus CNV' },
  { value: 'exomedepth', label: 'ExomeDepth' },
])

const COHORT_URLS = { 'genesus-cnv': 'cnv_cohorts', exomedepth: 'exomedepth_cohorts' }
const COHORT_METHOD_LABEL = Object.fromEntries(COHORT_METHOD_OPTIONS.map((o) => [o.value, o.label]))

/** The BedFile id for a kit held by its `name` (what the kit select holds), or null. */
export function bedFileIdOf(bedFiles, kitId) {
  if (!Array.isArray(bedFiles) || !kitId) return null
  const bed = bedFiles.find((b) => b && b.name === kitId)
  return bed && bed.id !== undefined && bed.id !== null ? bed.id : null
}

/**
 * The request the wizard would send to build a cohort.
 * @returns {{ url: string, body: { cohort_name, fastq_file_pair_id_list, ref_bed_file_id } }}
 */
export function buildCohortRequest({ method, name, pairIds, bedFileId } = {}) {
  const path = COHORT_URLS[method] || COHORT_URLS['genesus-cnv']
  return {
    url: `${API_BASE_URL}/${path}`,
    body: {
      cohort_name: trimmed(name),
      fastq_file_pair_id_list: Array.isArray(pairIds) ? pairIds : [],
      ref_bed_file_id: bedFileId,
    },
  }
}

/**
 * Problems that keep a cohort from starting. Every one blocks.
 * members = [{ pairId, key }] (key is the sample name; two members may not share one).
 * @returns {Array<{ code, message, blocking: true, pairId? }>}
 */
export function validateCohort({ method, name, kitId, bedFileId, members } = {}) {
  const issues = []
  const add = (code, message, extra = {}) => issues.push({ code, message, blocking: true, ...extra })
  const list = (Array.isArray(members) ? members : []).filter((m) => m && m.pairId !== undefined && m.pairId !== null)
  if (!COHORT_URLS[method]) add('bad_method', 'Choose a cohort method')
  if (!kitId) add('no_kit', 'Choose a capture kit')
  else if (bedFileId === undefined || bedFileId === null || bedFileId === '')
    add('kit_missing', 'Capture kit not found')
  if (!trimmed(name)) add('no_name', 'Name the cohort')
  if (list.length < COHORT_MIN) add('too_few', `Select at least ${COHORT_MIN} analyses`)
  if (list.length > COHORT_MAX) add('too_many', `${COHORT_MAX} analyses at most`)
  const seen = new Set()
  list.forEach((member) => {
    const key = trimmed(member.key).toLowerCase()
    if (key && seen.has(key)) add('duplicate_sample', 'Sample already selected', { pairId: member.pairId })
    if (key) seen.add(key)
  })
  return issues
}

// ---------------------------------------------------------------------------
// Carrier: one analysis per couple (or group) of samples on a carrier panel (the
// wizard's handleCarrierSubmit). The wizard's carrier flow has no configure step, so
// the reference, sequencing type and kit keep its defaults ('None', '', '') and CNV
// is off.

const carrierFileType = (fileType) => (normaliseFileType(fileType) === 'VCF' ? 'VCF' : 'FASTQ')

/**
 * The request the wizard would send for one carrier group, plus `batch` when given
 * (the backend stores the whole body as the analysis parameters).
 * config = { id, samples_required }; members in slot order: [{ fileId, fileType }].
 * With 2 samples required the first is maternal and the second paternal.
 * @returns {{ url: string, body: { annotation_type: 'CARRIER', carrierConfigId, annotation, batch? } }}
 */
export function buildCarrierRequest({ config, members, batch } = {}) {
  const couple = Number(config?.samples_required) === 2
  const annotation = (Array.isArray(members) ? members : [])
    .filter((member) => member && member.fileId)
    .map((member, index) => {
      const type = carrierFileType(member.fileType)
      return {
        type,
        fileId: member.fileId,
        ...(couple ? { role: index === 0 ? 'maternal' : 'paternal' } : {}),
        annotation: {
          reference: 'None',
          runAI: false,
          isSnp: true,
          isCnv: false,
          type: type === 'VCF' ? undefined : 'Targeted',
          targetedType: '',
          panel: '',
          analysisType: 'Carrier',
          runCnv: false,
        },
      }
    })
  const body = { annotation_type: 'CARRIER', carrierConfigId: config?.id, annotation }
  if (batch && batch.id) body.batch = { id: batch.id, name: batch.name || '' }
  return { url: startUrl(), body }
}

/**
 * Problems that keep a carrier group from starting. Every one blocks.
 * members in slot order, null for an empty slot. FASTQ members need allowFastq.
 * @returns {Array<{ code, message, blocking: true, slot? }>}
 */
export function validateCarrierGroup({ config, members, allowFastq = false } = {}) {
  const issues = []
  const add = (code, message, extra = {}) => issues.push({ code, message, blocking: true, ...extra })
  if (!config || config.id === undefined || config.id === null || config.id === '') {
    add('no_config', 'Choose a carrier panel')
    return issues
  }
  const slots = Array.isArray(members) ? members : []
  const required = Number(config.samples_required) || 0
  const filled = slots.filter((member) => member && member.fileId)
  if (required && filled.length !== required) add('wrong_count', `Needs ${required} samples`)
  const seen = new Set()
  slots.forEach((member, slot) => {
    if (!member || !member.fileId) {
      if (slot < required || !required) add('no_file', 'Choose a sample', { slot })
      return
    }
    const id = String(member.fileId)
    if (seen.has(id)) add('duplicate', 'Same sample twice', { slot })
    seen.add(id)
    if (!allowFastq && carrierFileType(member.fileType) === 'FASTQ') {
      add('fastq_unavailable', 'Carrier runs on VCFs for now', { slot })
    }
  })
  return issues
}

// ---------------------------------------------------------------------------
// Validation (mirrors the wizard's canProceed rules)

/**
 * Problems with the workflow itself, keyed by field, for inline errors. {} when valid.
 */
export function validateWorkflow(workflow) {
  const wf = withDefaults(workflow)
  const issues = {}
  // Group analyses: only what the workflow column holds. Members are checked by
  // validateMultisampleGroup, validateCohort and validateCarrierGroup.
  if (wf.analysis === 'multisample') return issues
  if (wf.analysis === 'cohort') {
    if (!COHORT_URLS[wf.cohortMethod]) issues.cohortMethod = 'Choose a cohort method'
    if (!wf.kitId) issues.kitId = 'Choose a capture kit'
    if (!trimmed(wf.cohortName)) issues.cohortName = 'Name the cohort'
    return issues
  }
  if (wf.analysis === 'carrier') {
    if (wf.carrierConfigId === null || wf.carrierConfigId === undefined || wf.carrierConfigId === '') {
      issues.carrierConfigId = 'Choose a carrier panel'
    }
    return issues
  }
  if (wf.analysis !== 'germline' && wf.analysis !== 'somatic') {
    issues.analysis = 'Choose an analysis'
    return issues
  }
  if (!INPUT_TO_ANNOTATION_TYPE[wf.input]) {
    issues.input = 'Choose an input'
    return issues
  }
  if (isSomatic(wf) && wf.input !== 'fastq' && wf.input !== 'vcf') {
    issues.input = 'Somatic runs take FASTQ or VCF'
    return issues
  }
  if (wf.input === 'fastq') {
    if (isSomatic(wf) && !['to', 'tn', 'rna'].includes(wf.mode)) {
      issues.mode = 'Choose tumour-only, tumour-normal or tumour RNA'
    }
    // Tumour RNA: no sequencing type, kit or CNV; the library is per sample.
    if (isTumourRna(wf)) return issues
    if (!SEQUENCING[wf.seq]) issues.seq = 'Choose a sequencing type'
    else if (isExome(wf.seq) && !wf.kitId) issues.kitId = 'Choose a capture kit'
    if (runCnvFor(wf, null)) {
      if (!CNV_TYPES[wf.cnvMethod]) issues.cnvMethod = 'Choose a CNV method'
      else if (wf.cnvMethod === 'exomedepth' && (!wf.cnvCohortId || wf.cnvCohortId === GENESUS_AUTOMATIC_COHORT)) {
        issues.cnvCohortId = 'ExomeDepth needs a finished cohort for this kit. Build one or choose Genesus CNV.'
      }
    }
  }
  if (wf.input === 'vcf' && !REFERENCE_GENOMES.some((ref) => ref.value === wf.reference)) {
    issues.reference = 'Choose a reference genome'
  }
  return issues
}

/**
 * Problems with one sample under this workflow.
 * @returns {Array<{ code: string, message: string, blocking: boolean, fix?: string }>}
 */
export function validateSample(workflow, sample, { rnaCapture = RNA_CAPTURE_OFF } = {}) {
  const wf = withDefaults(workflow)
  const issues = []
  const add = (code, message, extra = {}) => issues.push({ code, message, blocking: true, ...extra })

  if (!sample?.fileId) {
    add('no_file', 'No file for this sample')
    return issues
  }
  const expected = INPUT_TO_ANNOTATION_TYPE[wf.input]
  const actual = annotationTypeOf(wf, sample)
  if (expected && actual && expected !== actual) {
    add('type_mismatch', `${INPUT_LABELS[actual]} file, the workflow input is ${INPUT_LABELS[expected]}`)
    return issues
  }

  if (isSomatic(wf)) {
    const specimenErrors = validateSpecimen(specimenOf(sample))
    if (Object.keys(specimenErrors).length) {
      const labels = {
        tumourPercent: 'tumour %',
        ageYears: 'age',
        tumourCollectionDate: 'tumour collection date',
        normalCollectionDate: 'normal collection date',
      }
      const detail = Object.entries(specimenErrors).map(([field, msg]) => `${labels[field] || field}: ${msg}`)
      add('specimen', `Specimen ${detail.join('; ')}`)
    }
    if (actual === 'VCF' && vcfVariantType(sample) !== 'snp_indel') {
      add('somatic_vcf_type', 'Somatic runs take SNV/Indel VCFs only')
    }
    if (actual === 'FASTQ') {
      if (isTumourRna(wf) && laneCountOf(sample) > 1) {
        add('somatic_lanes', 'Tumour RNA takes one FASTQ pair per sample')
      } else if (typeof sample.lanes === 'number' && sample.lanes > laneIdsOf(sample).length) {
        add('lane_files_missing', 'Lane files not found for this sample')
      }
      if (isTumourRna(wf)) {
        const libraryErrors = validateLibrary(libraryOf(sample), rnaCapture)
        const labels = {
          libraryPrep: 'preparation',
          targetPanel: 'target panel',
          rin: 'RIN',
          dv200: 'DV200',
          inputNg: 'input',
          umiReadStructure: 'UMI read structure',
        }
        const detail = Object.entries(libraryErrors).map(([field, msg]) => `${labels[field] || field}: ${msg}`)
        if (detail.length) add('library', `RNA library ${detail.join('; ')}`)
      }
      if (wf.mode === 'tn') {
        if (!sample.normalFileId) add('no_normal', 'No normal sample')
        else if (normalLaneIdsOf(sample).some((id) => laneIdsOf(sample).includes(id))) {
          add('normal_is_tumour', 'Normal sample is the tumour sample')
        }
      }
    }
  } else if (actual === 'FASTQ' && typeof sample.lanes === 'number' && sample.lanes > laneIdsOf(sample).length) {
    add('lane_files_missing', 'Lane files not found for this sample')
  }

  if (runAIFor(wf, sample)) {
    const hasHpo = hpoTermsOf(sample).length > 0
    const hasHistory = typeof sample.clinicalHistory === 'string' && sample.clinicalHistory.trim() !== ''
    if (!hasHpo || !hasHistory) {
      add('ai_needs_phenotype', 'AI needs HPO terms and clinical history', { fix: 'runWithoutAI' })
    }
  }

  if (actual === 'PACBIO_BAM' && requestedLongRead(wf, sample).meth && !methylationReady(sample)) {
    issues.push({ code: 'methylation_not_ready', message: 'Methylation is not ready for this file', blocking: false })
  }
  return issues
}

// ---------------------------------------------------------------------------
// Descriptions and names

/** A short summary like "Germline · Whole exome · Twist Exome v2 · CNV". */
export function describeWorkflow(workflow) {
  const wf = withDefaults(workflow)
  if (wf.analysis === 'multisample') return 'Multisample'
  if (wf.analysis === 'cohort') {
    const kit = wf.kitName || wf.kitLabel || wf.kitId
    return ['CNV cohort', COHORT_METHOD_LABEL[wf.cohortMethod], kit].filter(Boolean).join(' · ')
  }
  if (wf.analysis === 'carrier') return ['Carrier', trimmed(wf.carrierConfigTitle)].filter(Boolean).join(' · ')
  const parts = [isSomatic(wf) ? 'Somatic' : 'Germline']
  if (wf.input === 'fastq') {
    if (isTumourRna(wf)) return [...parts, 'Tumour RNA'].join(' · ')
    if (isSomatic(wf)) parts.push(wf.mode === 'tn' ? 'Tumour-normal' : 'Tumour-only')
    if (SEQUENCING[wf.seq]) parts.push(SEQUENCING[wf.seq].label)
    const kit = wf.kitName || wf.kitLabel || wf.kitId
    if (isExome(wf.seq) && kit) parts.push(kit)
    if (runCnvFor(wf, null)) parts.push('CNV')
  } else if (wf.input === 'ont' || wf.input === 'pacbio') {
    parts.push(INPUT_LABELS[INPUT_TO_ANNOTATION_TYPE[wf.input]])
    if (wf.lrCnv) parts.push('CNV')
    if (wf.lrSv) parts.push('SV')
    if (wf.lrMeth) parts.push('Methylation')
  } else if (wf.input === 'vcf') {
    parts.push('VCF')
    if (hasReference(wf)) parts.push(wf.reference)
  }
  if (!isSomatic(wf) && wf.interpretation === 'ai') parts.push('AI-assisted')
  return parts.join(' · ')
}

/** An automatic batch name like "WES germline 29 Sep". */
export function suggestBatchName(workflow, date = new Date()) {
  const wf = withDefaults(workflow)
  // A fixed month list: en-GB in newer browsers writes September as "Sept".
  const day = `${date.getDate()} ${MONTHS[date.getMonth()]}`
  const group = { multisample: 'Multisample', cohort: 'Cohort', carrier: 'Carrier' }[wf.analysis]
  if (group) return `${group} ${day}`
  const input =
    wf.input === 'fastq'
      ? SEQUENCING[wf.seq]?.short || 'FASTQ'
      : wf.input === 'ont'
      ? 'ONT'
      : wf.input === 'pacbio'
      ? 'PacBio'
      : 'VCF'
  return `${input} ${isSomatic(wf) ? 'somatic' : 'germline'} ${day}`
}

/** A new batch with a client-side uuid. */
export const newBatch = (name) => ({ id: uuidv4(), name: name || '' })

// ---------------------------------------------------------------------------
// Price estimate (mirrors the balance check in GenericAnnotateView)

const priceOf = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Credits one sample will cost, from /organisation/details, or null when unknown.
 * ONT: ont_bam_price, plus ai_price with AI. PacBio: pacbio_bam_price, plus cnv_price
 * when the sample's CNV runs, plus ai_price with AI (GenericAnnotateView). Both are
 * null until /organisation/details exposes those fields.
 */
export function estimateSamplePrice(workflow, sample, organisation) {
  if (!organisation) return null
  const wf = withDefaults(workflow)
  if (isGroupWorkflow(wf)) return null
  const type = annotationTypeOf(wf, sample)
  let price
  if (type === 'ONT_BAM' || type === 'PACBIO_BAM') {
    price = priceOf(organisation[type === 'ONT_BAM' ? 'ont_bam_price' : 'pacbio_bam_price'])
    if (price === null) return null
    if (type === 'PACBIO_BAM' && effectiveLongRead(wf, sample).cnv) {
      const cnv = priceOf(organisation.cnv_price)
      if (cnv === null) return null
      price += cnv
    }
  } else if (type === 'VCF') {
    price = priceOf(organisation.vcf_price)
  } else if (type === 'FASTQ') {
    const field =
      wf.seq === 'wgs'
        ? 'whole_genome_fastq_price'
        : wf.seq === 'wes'
        ? 'targeted_whole_exome_fastq_price'
        : wf.seq === 'ces'
        ? 'targeted_clinical_exome_fastq_price'
        : null
    price = field ? priceOf(organisation[field]) : null
  } else {
    return null
  }
  if (price === null) return null
  if (type === 'FASTQ' && runCnvFor(wf, sample)) {
    const cnv = priceOf(organisation.cnv_price)
    if (cnv === null) return null
    price += cnv
  }
  if (runAIFor(wf, sample)) {
    const ai = priceOf(organisation.ai_price)
    if (ai === null) return null
    price += ai
  }
  return price
}

/** Credits one carrier analysis costs (the whole group), or null when not exposed. */
export function estimateCarrierPrice(organisation) {
  return organisation ? priceOf(organisation.carrier_price) : null
}

/** { total, unknown }: the summed estimate and how many samples have no known price. */
export function estimateTotalPrice(workflow, samples, organisation) {
  return (samples || []).reduce(
    (acc, sample) => {
      const price = estimateSamplePrice(workflow, sample, organisation)
      if (price === null) return { ...acc, unknown: acc.unknown + 1 }
      return { ...acc, total: acc.total + price }
    },
    { total: 0, unknown: 0 },
  )
}

// ---------------------------------------------------------------------------
// Patient fields

export const hasPatientChanges = (sample) => PATIENT_FIELDS.some((field) => Boolean(sample?.changed?.[field]))

const toWholeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? Math.trunc(n) : null
}

const normaliseSex = (value) => {
  if (value === null || value === undefined || value === '') return null
  const raw = String(value).trim()
  if (KARYOTYPES.includes(raw.toUpperCase())) return raw.toUpperCase()
  const lower = raw.toLowerCase()
  if (lower === 'female' || lower === 'f') return 'XX'
  if (lower === 'male' || lower === 'm') return 'XY'
  return null
}

/**
 * Saves sex, age, age at onset and notes on the sample's main file through
 * POST /file/details, the endpoint SampleDetailsPanel uses, so the analysis copy made
 * at start inherits them. Only fields in sample.changed are written.
 *
 * /file/details overwrites all six of its fields and needs every key, so the current
 * values are read first (GET /file/:id) and only the changed ones replaced; otherwise
 * saving the age would wipe consanguinity, progression and anything else unchanged.
 * FASTQ and VCF only: that endpoint cannot write ONT or PacBio files.
 *
 * @returns {Promise<{ saved: boolean, reason?: 'unchanged' | 'unsupported' }>}
 */
export async function savePatientFields(sample) {
  if (!hasPatientChanges(sample)) return { saved: false, reason: 'unchanged' }
  const type = normaliseFileType(sample.fileType)
  if (!DETAILS_FILE_TYPES.includes(type)) return { saved: false, reason: 'unsupported' }
  if (!sample.fileId) throw new Error('No file for this sample')

  const { data: file } = await axios.get(`${API_BASE_URL}/file/${sample.fileId}`)
  const current = file && file.details
  if (!current) throw new Error('Could not read the current patient details')

  const details = {
    gender: current.sex ?? null,
    start_age: current.symptoms_start_age ?? null,
    age: current.age ?? null,
    is_inbred: Boolean(current.is_inbred),
    is_progressing: Boolean(current.is_progressing),
    notes: current.notes ?? '',
  }
  const { changed } = sample
  if (changed.sex) details.gender = normaliseSex(sample.sex)
  if (changed.age) details.age = toWholeNumber(sample.age)
  if (changed.onsetAge) details.start_age = toWholeNumber(sample.onsetAge)
  if (changed.notes) details.notes = String(sample.notes ?? '').slice(0, NOTES_MAX_LENGTH)

  await updateDetails(sample.fileId, details, type)
  return { saved: true }
}

// ---------------------------------------------------------------------------
// Start loop

export const START_ERRORS = {
  insufficient_balance: 'Not enough balance',
  cancelled: 'Stopped before it started',
  no_reply: 'No reply from the server. It may have started. Check Analysis History before retrying.',
}

// A start that got no reply (network error, timeout) or a gateway error may still
// have reached the server and started, so its outcome is unknown rather than failed.
// Retrying it blind could start (and charge) the same analysis twice.
const GATEWAY_STATUSES = [502, 503, 504]
export const isNoReply = (error) => !error?.response || GATEWAY_STATUSES.includes(error.response.status)

// A clean refusal before anything is written or charged (400 with a reason
// code), for example RNA analysis switched off in this environment, or a FASTQ
// lane that is gone or not this organisation's (404 lane_not_found). It reads as
// a plain note with the served sentence, never as an error.
export const isRefusal = (error) => {
  const data = error?.response?.data
  const status = error?.response?.status
  if (!(data && typeof data === 'object' && data.code && data.error)) return false
  return status === 400 || (status === 404 && data.code === 'lane_not_found')
}

const serverMessage = (error) => {
  const data = error?.response?.data
  // A file the backend could not import: the one neutral sentence, never its reason.
  if (data && typeof data === 'object' && isImportRefusal(data.code, data.error)) return IMPORT_FAILED_TEXT
  if (data && typeof data === 'object' && typeof data.error === 'string' && data.error) return data.error
  if (typeof data === 'string' && data && data.length <= 200 && !/<[a-z!]/i.test(data)) return data
  if (error?.response) return `Server error ${error.response.status}`
  return 'No response from server'
}

/**
 * The text of a clean refusal (400 with a reason code) for screens outside the New
 * analysis flow, for example a panel whose library chemistry cannot be analysed
 * ("amplicon_mode_unavailable"): the server's clinician text, or the one neutral
 * sentence for a file that could not be imported. null for any other error.
 */
export const refusalTextOf = (error) => (isRefusal(error) ? serverMessage(error) : null)

// onProgress wrapped so a failing callback never stops a start loop.
const notifier = (onProgress, label) => (index, status, detail) => {
  if (!onProgress) return
  try {
    onProgress(index, status, detail)
  } catch (error) {
    console.error(`${label}: onProgress failed`, error)
  }
}

const CANCELLED = Object.freeze({ code: 'cancelled', error: START_ERRORS.cancelled })

/**
 * Posts one prebuilt request and sorts out what happened. Never throws.
 * `halt` is set on a 402: the caller stops and skips the rest.
 * @returns {Promise<{ status: 'started'|'failed'|'unknown', detail: object, halt?: object }>}
 */
const postStart = async (request) => {
  try {
    const { data } = await axios.post(request.url, request.body)
    return { status: 'started', detail: { data } }
  } catch (error) {
    if (error?.response?.status === 402) {
      const halt = { code: 'insufficient_balance', error: START_ERRORS.insufficient_balance }
      return { status: 'failed', halt, detail: { ...halt, neededBalance: error.response.data?.needed_balance ?? null } }
    }
    if (isNoReply(error)) return { status: 'unknown', detail: { code: 'no_reply', error: START_ERRORS.no_reply } }
    if (isRefusal(error)) {
      return {
        status: 'failed',
        detail: {
          code: 'refused',
          reasonCode: error.response.data.code,
          error: serverMessage(error),
          ...(isImportRefusal(error.response.data.code, error.response.data.error) ? { importRefused: true } : {}),
        },
      }
    }
    const importRefused = isImportRefusal(error?.response?.data?.code, error?.response?.data?.error)
    return {
      status: 'failed',
      detail: { code: 'error', error: serverMessage(error), ...(importRefused ? { importRefused } : {}) },
    }
  }
}

/**
 * Starts one analysis per sample, one request at a time.
 *
 * onProgress(index, status, detail) is called with index into `samples` and status:
 *   'starting'  the sample's request is being sent
 *   'started'   detail.data is the server response ({ new_balance, deducted_amount })
 *   'failed'    it was tried (or is invalid) and did not start; detail.error, detail.code
 *   'unknown'   sent, but no reply came back (or a 502, 503 or 504): it may have
 *               started. detail.code 'no_reply', detail.error START_ERRORS.no_reply
 *   'skipped'   never sent because the loop stopped (not enough balance, or signal)
 * detail also carries sampleKey, and `warning` when the patient details could not be
 * saved (the analysis still starts; they can be edited on the sample later).
 *
 * The loop stops at the first 402: that sample is 'failed' and every later one
 * 'skipped', both with code 'insufficient_balance' ("Not enough balance"); the 402
 * sample also has neededBalance. Other errors mark only that sample and continue.
 * Aborting `signal` stops before the next sample (code 'cancelled').
 *
 * Group analyses (multisample, cohort, carrier) do not start per sample: every
 * sample is 'failed' with code 'invalid'. They start through startRequests.
 *
 * @returns {Promise<Array<{ sampleKey, status: 'started'|'failed'|'unknown'|'skipped', error?, code?, data?, warning?, neededBalance? }>>}
 */
export async function startAnalyses(
  { workflow, samples = [], batch, rnaCapture = RNA_CAPTURE_OFF } = {},
  { onProgress, signal } = {},
) {
  const wf = withDefaults(workflow)
  const results = []
  const notify = notifier(onProgress, 'startAnalyses')
  const workflowError = isGroupWorkflow(wf)
    ? 'This analysis starts one request per group'
    : Object.values(validateWorkflow(wf))[0] || null
  let halt = null

  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index]
    const record = (status, extra = {}) => {
      const result = { sampleKey: sample?.key ?? null, status, ...extra }
      results.push(result)
      notify(index, status, result)
    }

    if (workflowError) {
      record('failed', { code: 'invalid', error: workflowError })
      continue
    }
    if (!halt && signal?.aborted) halt = CANCELLED
    if (halt) {
      record('skipped', halt)
      continue
    }
    const problem = validateSample(wf, sample, { rnaCapture }).find((issue) => issue.blocking)
    if (problem) {
      record('failed', { code: 'invalid', error: problem.message })
      continue
    }

    notify(index, 'starting', { sampleKey: sample.key ?? null })

    let warning
    try {
      const saved = await savePatientFields({ ...sample, fileType: annotationTypeOf(wf, sample) })
      // Changed, but the details endpoint cannot write this file type (ONT, PacBio).
      if (saved && saved.reason === 'unsupported') warning = 'Patient details not saved'
    } catch (error) {
      console.error('startAnalyses: patient details not saved', error)
      warning = 'Patient details not saved'
    }
    if (signal?.aborted) {
      halt = CANCELLED
      record('skipped', halt)
      continue
    }

    let request
    try {
      request = buildStartRequest(wf, sample, batch, { rnaCapture })
    } catch (error) {
      // Never sent, so it certainly did not start.
      console.error('startAnalyses: request not built', error)
      record('failed', { code: 'error', error: 'Could not prepare the request', ...(warning ? { warning } : {}) })
      continue
    }

    const outcome = await postStart(request)
    if (outcome.halt) {
      // The 402 sample carries the balance, not the details warning.
      halt = outcome.halt
      record(outcome.status, outcome.detail)
    } else {
      record(outcome.status, { ...outcome.detail, ...(warning ? { warning } : {}) })
    }
  }
  return results
}

/**
 * Posts prebuilt requests (multisample groups, a cohort, carrier groups) one at a
 * time, with the same outcomes as startAnalyses:
 *   items = [{ key, request?: { url, body }, error?: string }]
 *   an item with `error` (or no request) is 'failed' with code 'invalid' and not sent;
 *   a 402 is 'failed' with code 'insufficient_balance' and neededBalance, and every
 *   later item 'skipped' with the same code; no reply or a 502/503/504 is 'unknown'
 *   with code 'no_reply'; other errors are 'failed' with code 'error' and the server's
 *   message; an aborted `signal` skips the rest with code 'cancelled'.
 * onProgress(index, status, detail) as in startAnalyses, detail carrying `key`.
 * @returns {Promise<Array<{ key, status: 'started'|'failed'|'unknown'|'skipped', error?, code?, data?, neededBalance? }>>}
 */
export async function startRequests(items = [], { onProgress, signal } = {}) {
  const results = []
  const notify = notifier(onProgress, 'startRequests')
  let halt = null
  const list = Array.isArray(items) ? items : []

  for (let index = 0; index < list.length; index += 1) {
    const item = list[index]
    const key = item?.key ?? null
    const record = (status, extra = {}) => {
      const result = { key, status, ...extra }
      results.push(result)
      notify(index, status, result)
    }

    if (!halt && signal?.aborted) halt = CANCELLED
    if (halt) {
      record('skipped', halt)
      continue
    }
    if (item?.error || !item?.request?.url) {
      record('failed', { code: 'invalid', error: item?.error || 'Could not prepare the request' })
      continue
    }

    notify(index, 'starting', { key })
    const outcome = await postStart(item.request)
    if (outcome.halt) halt = outcome.halt
    record(outcome.status, outcome.detail)
  }
  return results
}
